import { createFileRoute, Link } from '@tanstack/react-router';
import {
  BookOpen,
  Building2,
  CreditCard,
  FileSearch,
  KeyRound,
  Plug,
  Scale,
  ShieldCheck,
  Webhook,
} from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export const Route = createFileRoute('/_authed/guide')({
  component: GuidePage,
});

function GuidePage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <div className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
          <BookOpen className="h-3.5 w-3.5" />
          {t('guide.title')} · v1.2
        </div>
        <h1 className="text-3xl font-semibold tracking-tight">{t('guide.title')}</h1>
        <p className="max-w-3xl text-muted-foreground">{t('guide.subtitle')}</p>
      </header>

      <div className="grid gap-4 md:grid-cols-3">
        <QuickCard
          to="/merchants"
          icon={Building2}
          title={`1. ${t('merchants.create')}`}
          body={t('guide.quickstart_steps') as string}
        />
        <QuickCard
          to="/integrations"
          icon={Plug}
          title={`2. ${t('nav.psp_integrations')}`}
          body={t('psp_guide.subtitle') as string}
        />
        <QuickCard
          to="/payments"
          icon={CreditCard}
          title={`3. ${t('nav.payments')}`}
          body={t('payments.subtitle') as string}
        />
      </div>

      <Section
        icon={KeyRound}
        title={t('guide.section_auth') as string}
        items={[
          {
            label: 'Customer API',
            body: (
              <>
                Customer-facing requests on <code>/v1/*</code> authenticate with{' '}
                <code>X-API-Key: &lt;merchant-api-key&gt;</code>. Keys are merchant-scoped and
                can be revoked from the merchant detail page; revocation is immediate.
              </>
            ),
          },
          {
            label: 'Admin UI',
            body: (
              <>
                Operators sign in with username + password (Argon2id-hashed). MFA is optional;
                enrol from <Link to="/security" className="text-primary underline-offset-4 hover:underline">Security</Link>.
                Sessions are itsdangerous-signed cookies — 15 min idle timeout, 8 h absolute,
                refreshed on each authenticated request.
              </>
            ),
          },
        ]}
      />

      <Section
        icon={Building2}
        title={t('guide.section_merchants') as string}
        items={[
          {
            label: 'Create a merchant',
            body: (
              <>
                <Link to="/merchants" className="text-primary underline-offset-4 hover:underline">
                  Merchants
                </Link>{' '}
                → <em>Add merchant</em>. Pick a slug (lowercase, dashes); you'll use it in
                webhook URLs and audit-log filters.
              </>
            ),
          },
          {
            label: 'Issue an API key',
            body: 'On the merchant page, open the API keys tab → Create. The plaintext secret is displayed once in a copy dialog — store it in the merchant’s vault before dismissing. PayHub stores only an Argon2id hash; we cannot recover the secret.',
          },
          {
            label: 'Revoke',
            body: 'Revocation is instant. Any in-flight requests with the revoked key get a 401 on next call. Roll keys regularly; PayHub records every key creation/revocation in the audit log.',
          },
        ]}
      />

      <Section
        icon={Plug}
        title={t('guide.section_gateways') as string}
        items={[
          {
            label: 'Per-merchant configuration',
            body: 'Each merchant configures the PSPs it actually uses. The form on the gateway tab is generated from the PSP’s schema (see app/services/gateway_config.py::_SCHEMAS) so missing or malformed values are caught before the credential is stored.',
          },
          {
            label: 'Encryption at rest',
            body: 'Credentials are encrypted with a per-row DEK that is itself wrapped by the master KEK. Rotating the KEK only re-wraps the DEKs — the secrets never decrypt to disk.',
          },
          {
            label: 'Test button',
            body: 'The Test action runs a non-mutating preflight against the PSP (e.g. a dummy auth call for Sadad, a Filter request with a 0-row window for Moamalat). Use it after every credential change.',
          },
        ]}
      />

      <Section
        icon={CreditCard}
        title={t('guide.section_payments') as string}
        items={[
          {
            label: 'States',
            body: (
              <>
                <code>pending → requires_action → processing → succeeded | failed | expired</code>.
                A succeeded payment can transition to <code>refunded</code>. Every transition
                is recorded as a <code>payment_event</code>; the row is just a projection,
                which is what makes replay safe.
              </>
            ),
          },
          {
            label: 'Filtering',
            body: 'The list page accepts PSP and status filters; both are stored in the URL so any view is shareable. The detail view exposes the event timeline, the raw PSP payload, and any related webhook deliveries.',
          },
          {
            label: 'Idempotency',
            body: (
              <>
                Customer initiates accept an <code>Idempotency-Key</code> header. Replays
                within 24 h return the original response without contacting the PSP again.
              </>
            ),
          },
        ]}
      />

      <Section
        icon={Webhook}
        title={t('guide.section_webhooks') as string}
        items={[
          {
            label: 'Outbound deliveries',
            body: (
              <>
                Configured per merchant under the merchant detail page. The hub signs each
                delivery with an HMAC-SHA256 over the raw body; verify with the secret you
                set on creation. Receivers should respond <code>2xx</code> within 10 seconds —
                anything else triggers exponential backoff up to 7 attempts, then the row is
                marked <code>failed</code> and surfaced for replay.
              </>
            ),
          },
          {
            label: 'Replay & resolve',
            body: (
              <>
                On the{' '}
                <Link to="/webhooks/deliveries" className="text-primary underline-offset-4 hover:underline">
                  Deliveries
                </Link>{' '}
                page, use <em>Replay</em> to re-enqueue a failed delivery, or <em>Resolve</em>{' '}
                to mark it manually fixed (e.g. the consumer was patched out-of-band).
                Both actions are recorded in the audit log.
              </>
            ),
          },
          {
            label: 'Inbound (PSP callbacks)',
            body: (
              <>
                PSPs post to <code>/v1/callbacks/&lt;psp&gt;/&lt;merchant_slug&gt;</code>.
                The hub verifies the signature, parses the body, then matches the payment by{' '}
                <code>psp_ref</code> or <code>merchant_ref</code> — whichever the PSP echoed
                back. Both bare HTTP and form-encoded T-Lync callbacks are supported.
              </>
            ),
          },
        ]}
      />

      <Section
        icon={Scale}
        title={t('guide.section_settlements') as string}
        items={[
          {
            label: 'File ingest',
            body: (
              <>
                Drop a settlement CSV on the{' '}
                <Link to="/settlements" className="text-primary underline-offset-4 hover:underline">
                  Settlements
                </Link>{' '}
                page. The reconciler classifies every row as{' '}
                <Badge variant="secondary">matched</Badge>{' '}
                <Badge variant="secondary">mismatch</Badge>{' '}
                <Badge variant="secondary">missing_in_hub</Badge> or{' '}
                <Badge variant="secondary">missing_in_psp</Badge> and back-fills missing{' '}
                <code>psp_ref</code> values.
              </>
            ),
          },
          {
            label: 'Auto-pull',
            body: 'For PSPs that publish a programmatic export (Moamalat Filter API today), PayHub pulls yesterday’s window every night at 02:15 UTC. Use Pull from PSP for an ad-hoc pull. File hashes are deduped, so a re-run with the same data is a safe no-op.',
          },
        ]}
      />

      <Section
        icon={FileSearch}
        title={t('guide.section_audit') as string}
        items={[
          {
            label: 'What gets logged',
            body: 'Every admin login, MFA enrol/disable, merchant CRUD, API-key issue/revoke, gateway upsert/test/deactivate, and webhook replay/resolve. Auto-pulled settlements are tagged uploaded_by="system:settlement_puller" so they’re distinguishable from human uploads.',
          },
          {
            label: 'Filtering',
            body: (
              <>
                The{' '}
                <Link to="/audit" className="text-primary underline-offset-4 hover:underline">
                  Audit log
                </Link>{' '}
                page filters by action prefix, target type, and target id. Each row expands
                to show the before/after JSON — useful for compliance review.
              </>
            ),
          },
        ]}
      />

      <Section
        icon={ShieldCheck}
        title={t('guide.section_security') as string}
        items={[
          {
            label: 'PCI DSS posture',
            body: 'Target is SAQ-A: PayHub never stores or transmits PAN/CVV. Card-present flows go through Moamalat’s LightBox iframe; PSP-hosted flows redirect off-domain. The hub only sees psp_ref / status callbacks.',
          },
          {
            label: 'WAF',
            body: 'The edge runs nginx + ModSecurity v3 + OWASP CRS v4. Inbound anomaly threshold 5; rules log to /var/log/nginx/modsec_audit.log inside the WAF container. Whitelist exclusions live in deploy/nginx/modsec/exclusions/payhub.conf.',
          },
          {
            label: 'Reporting an incident',
            body: 'Email security@payhub.local with the audit-log timestamps, request IDs (every response carries X-Request-ID), and any PSP correlation IDs you have. Do not redact secrets — internal vault access only.',
          },
        ]}
      />
    </div>
  );
}

function QuickCard({
  to,
  icon: Icon,
  title,
  body,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
}) {
  return (
    <Link
      to={to}
      className="group rounded-xl border border-border bg-card p-5 shadow-soft transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-elev"
    >
      <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <div className="font-semibold tracking-tight group-hover:text-primary">{title}</div>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
    </Link>
  );
}

function Section({
  icon: Icon,
  title,
  items,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  items: { label: string; body: React.ReactNode }[];
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-3 space-y-0">
        <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </div>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="divide-y divide-border">
          {items.map((it) => (
            <div key={it.label} className="grid gap-1.5 py-3 sm:grid-cols-[180px_1fr] sm:gap-4">
              <dt className="text-sm font-medium text-foreground">{it.label}</dt>
              <dd className="text-sm leading-relaxed text-muted-foreground [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[12px] [&_code]:text-foreground">
                {it.body}
              </dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}
